<template>
  <div class="dialog-container" v-loading="isLoading">
    <el-alert class="mb-3" :title="errorMessage" type="error" show-icon v-if="errorMessage"></el-alert>
    <el-row :gutter="18">
      <el-col :span="24">
        <el-form ref="editPaystub" :model="form" :rules="rules" label-width="175px">
          <el-popover
            ref="medicareNotification"
            placement="top-start"
            width="200"
            trigger="hover"
            v-model="medicarePopoverVisible"
            >
            <p>{{ useMedicareTax ? 'No Medicare Tax? Enter Social Security Tax instead.' : 'Switch back to Medicare Tax.' }}</p>
            <el-button size="mini" type="text" @click="medicarePopoverVisible = false">Cancel</el-button>
            <el-button type="primary" size="mini" @click="toggleSSTaxAndMedicare">Switch</el-button>
          </el-popover>
          <el-form-item label="Medicare Tax" prop="medicareTax">
            <template slot="label">{{ useMedicareTax ? 'Medicare Tax' : 'Social Security Tax' }} <i v-popover:medicareNotification class="el-icon-info"></i></template>
            <el-input v-model="form.medicareTax" type="number" placeholder="Medicare Tax" v-show="useMedicareTax">
              <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
            </el-input>
            <el-input v-model="sstax" type="number" placeholder="Social Security Tax" v-show="!useMedicareTax">
              <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
            </el-input>
          </el-form-item>
          <el-form-item label="Federal Withholdings" prop="federalTax">
            <el-input v-model="form.federalTax" type="number" placeholder="Federal Withholdings">
              <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
            </el-input>
          </el-form-item>
          <el-form-item label="401(K) and 403(B) Contributions" prop="retirementAmt">
            <el-input v-model="form.retirementAmt" type="number" placeholder="401(K) and 403(B) Contributions">
              <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
            </el-input>
          </el-form-item>
          <el-form-item label="State Withholdings" prop="stateTax">
            <el-input v-model="form.stateTax" type="number" placeholder="State Withholdings">
              <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
            </el-input>
          </el-form-item>
          <el-form-item label="State" prop="state">
            <us-state-select v-model="form.state"></us-state-select>
          </el-form-item>
          <el-form-item label="Paystub Date" prop="paystubDate">
            <el-date-picker v-model="form.paystubDate" type="date" placeholder="Date Issued" format="MM/dd/yyyy" :clearable="false">
            </el-date-picker>
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>
    <el-row :gutter="18" class="dialog-footer">
      <el-col :span="24" class="align-right">
        <el-button @click="closeDialog">Cancel</el-button>
        <el-button type="primary" @click="saveChanges">Save Paystub</el-button>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import get from 'lodash/get';
import { Required } from '@/util/formValidators';
import Paystub from '@/model/Paystub';
import UsStateSelect from '@/components/form-item/USStateSelect.vue';

export default {
  props: {
    paystub: {
      type: Object,
      default() {
        return null;
      },
    },
    keepLocal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      medicarePopoverVisible: false,
      useMedicareTax: true,
      sstax: undefined,
      form: new Paystub(),
      rules: {
        medicareTax: [Required],
        federalTax: [Required],
        stateTax: [Required],
        state: [{ trigger: 'change', ...Required }],
        paystubDate: [{ type: 'date', ...Required }],
      },
    };
  },
  methods: {
    ...mapActions('Client', [
      'savePaystub',
    ]),
    saveChanges() {
      this.errorMessage = null;
      this.$refs.editPaystub.validate((valid) => {
        if (valid) {
          if (this.keepLocal) {
            this.$emit('paystubAdded', { ...this.form });
            this.closeDialog();
          } else {
            this.uploadPaystub();
          }
          return true;
        }
        return false;
      });
    },
    uploadPaystub() {
      this.isLoading = true;
      this.savePaystub(this.form).then(() => {
        this.isLoading = false;
        this.$notify.success({
          title: 'Success',
          message: 'Paystub saved.',
          position: 'bottom-right',
        });
        this.closeDialog();
      }).catch((e) => {
        this.isLoading = false;
        this.errorMessage = get(e, 'response.data', 'An error occurred.');
      });
    },
    closeDialog() {
      this.clearForm();
      this.$emit('close');
    },
    setFormFromPaystub(paystub) {
      if (paystub) {
        const formData = new Paystub(this.paystub);
        // format date
        if (formData.paystubDate) {
          formData.paystubDate = moment(formData.paystubDate).toDate();
        }
        // using object.assign here because it triggers the setter
        // using spread operator did not update date-input
        this.$nextTick(() => {
          Object.assign(this.form, formData);
        });
      } else {
        this.clearForm();
      }
    },
    clearForm() {
      Object.keys(this.form).forEach((key) => {
        this.form[key] = null;
      });
      this.sstax = undefined;
      this.$refs.editPaystub.resetFields();
    },
    toggleSSTaxAndMedicare() {
      this.useMedicareTax = !this.useMedicareTax;
      this.medicarePopoverVisible = false;
    },
  },
  watch: {
    paystub(val) {
      this.setFormFromPaystub(val);
    },
    sstax(val) {
      this.form.medicareTax = Math.round(((val / 0.0765) * 0.0145) * 100) / 100;
    },
  },
  mounted() {
    if (this.paystub) {
      this.setFormFromPaystub(this.paystub);
    }
  },
  components: {
    UsStateSelect,
  },
};

</script>
